<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick, computed } from "vue";
import { useTranslation } from "i18next-vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import { useDeviceTilt } from "@/composable/useDeviceTilt";
import { drawRotated } from "@/utils/imageOrientation";

const emit = defineEmits<{
  (e: "close"): void;
  (e: "captured", files: File[]): void;
}>();

const { t } = useTranslation();

type State = "loading" | "ready" | "error";

interface CapturedPhoto {
  file: File;
  previewUrl: string;
}

const state = ref<State>("loading");
const errorMessage = ref("");
const videoRef = ref<HTMLVideoElement | null>(null);
const canvasRef = ref<HTMLCanvasElement | null>(null);
const capturedPhotos = ref<CapturedPhoto[]>([]);
const isCapturing = ref(false);

let stream: MediaStream | null = null;

const hasPhotos = computed(() => capturedPhotos.value.length > 0);

const tilt = useDeviceTilt();

const startCamera = async () => {
  try {
    console.log("Requesting camera access...");
    stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: { ideal: "environment" }, // Back camera preferred
        width: { ideal: 1920 },
        height: { ideal: 1080 },
      },
      audio: false,
    });
    console.log("Got stream:", stream);

    if (videoRef.value) {
      videoRef.value.srcObject = stream;

      // Wait for video to be ready
      videoRef.value.onloadedmetadata = () => {
        console.log("Video metadata loaded");
        videoRef.value
          ?.play()
          .then(() => {
            console.log("Video playing");
            state.value = "ready";
          })
          .catch((err) => {
            console.error("Play error:", err);
            errorMessage.value = t("dashboard:fieldObservations.camera.error");
            state.value = "error";
          });
      };
    } else {
      console.error("videoRef is null");
      errorMessage.value = t("dashboard:fieldObservations.camera.error");
      state.value = "error";
    }
  } catch (error) {
    console.error("Camera error:", error);
    if (error instanceof DOMException) {
      if (error.name === "NotAllowedError") {
        errorMessage.value = t("dashboard:fieldObservations.camera.denied");
      } else if (error.name === "NotFoundError") {
        errorMessage.value = t("dashboard:fieldObservations.camera.notFound");
      } else {
        errorMessage.value = t("dashboard:fieldObservations.camera.error");
      }
    } else {
      errorMessage.value = t("dashboard:fieldObservations.camera.error");
    }
    state.value = "error";
  }
};

const stopCamera = () => {
  if (stream) {
    stream.getTracks().forEach((track) => track.stop());
    stream = null;
  }
};

const capturePhoto = async () => {
  if (!videoRef.value || !canvasRef.value || isCapturing.value) return;

  isCapturing.value = true;

  const video = videoRef.value;
  const canvas = canvasRef.value;

  // L'image du flux suit le sens de l'écran, pas celui de la tablette : on la
  // remet dans le sens du monde (gravité) au moment de la capture.
  await tilt.ensureReading();
  drawRotated(
    canvas,
    video,
    video.videoWidth,
    video.videoHeight,
    tilt.currentCorrection(),
  );

  // Convert canvas to blob
  canvas.toBlob(
    (blob) => {
      if (blob) {
        // Use date format instead of timestamp to avoid number parsing issues in backend
        const now = new Date();
        const dateStr = now.toISOString().replace(/[:.]/g, "-").slice(0, 19);
        const file = new File([blob], `capture_${dateStr}.jpg`, {
          type: "image/jpeg",
        });
        const previewUrl = URL.createObjectURL(blob);
        capturedPhotos.value.push({ file, previewUrl });
      }
      isCapturing.value = false;
    },
    "image/jpeg",
    0.9,
  );
};

const removePhoto = (index: number) => {
  const photo = capturedPhotos.value[index];
  URL.revokeObjectURL(photo.previewUrl);
  capturedPhotos.value.splice(index, 1);
};

const validatePhotos = () => {
  stopCamera();
  const files = capturedPhotos.value.map((p) => p.file);
  // Revoke all preview URLs
  capturedPhotos.value.forEach((p) => URL.revokeObjectURL(p.previewUrl));
  emit("captured", files);
};

const handleClose = () => {
  // Revoke all preview URLs
  capturedPhotos.value.forEach((p) => URL.revokeObjectURL(p.previewUrl));
  stopCamera();
  emit("close");
};

onMounted(async () => {
  await nextTick();
  startCamera();
});

onUnmounted(() => {
  capturedPhotos.value.forEach((p) => URL.revokeObjectURL(p.previewUrl));
  stopCamera();
});
</script>

<template>
  <div class="camera-overlay">
    <!-- Video preview (always rendered but hidden when not ready) -->
    <video
      ref="videoRef"
      class="camera-video"
      :class="{ hidden: state !== 'ready' }"
      playsinline
      webkit-playsinline
      muted
      autoplay
    />

    <!-- Loading state -->
    <div v-if="state === 'loading'" class="camera-loading">
      <div class="spinner-border text-light" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p>{{ t("dashboard:fieldObservations.camera.loading") }}</p>
    </div>

    <!-- Error state -->
    <div v-else-if="state === 'error'" class="camera-error">
      <div class="error-icon">
        <PhosphorIcon name="video-camera-slash" :size="36" />
      </div>
      <p>{{ errorMessage }}</p>
      <button class="btn-close-camera" @click="handleClose">
        {{ t("close") }}
      </button>
    </div>

    <!-- Captured photos preview strip -->
    <div v-if="state === 'ready' && hasPhotos" class="photos-strip">
      <div
        v-for="(photo, index) in capturedPhotos"
        :key="index"
        class="photo-thumbnail"
      >
        <img :src="photo.previewUrl" alt="" />
        <button class="btn-remove-photo" @click="removePhoto(index)">
          <PhosphorIcon name="x" :size="14" weight="bold" />
        </button>
      </div>
    </div>

    <!-- Controls (only when ready) -->
    <div v-if="state === 'ready'" class="camera-controls">
      <button class="btn-close-camera" @click="handleClose">
        <PhosphorIcon name="x" :size="24" weight="bold" />
      </button>

      <button
        class="btn-capture"
        :class="{ capturing: isCapturing }"
        @click="capturePhoto"
      >
        <div class="capture-ring">
          <div class="capture-center" />
        </div>
      </button>

      <button v-if="hasPhotos" class="btn-validate" @click="validatePhotos">
        <PhosphorIcon name="check" :size="24" weight="bold" />
        <span class="photo-count">{{ capturedPhotos.length }}</span>
      </button>
      <div v-else class="spacer" />
    </div>

    <!-- Hidden canvas for capture -->
    <canvas ref="canvasRef" class="d-none" />
  </div>
</template>

<style scoped lang="scss">
.camera-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background-color: black;
  display: flex;
  flex-direction: column;
}

.camera-loading,
.camera-error {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  color: white;
  text-align: center;
  padding: 24px;
}

.error-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 36px;
}

.camera-video {
  flex: 1;
  width: 100%;
  object-fit: cover;

  &.hidden {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }
}

.photos-strip {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  gap: 8px;
  padding: 16px;
  padding-top: max(16px, env(safe-area-inset-top));
  background: linear-gradient(rgba(0, 0, 0, 0.6), transparent);
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }
}

.photo-thumbnail {
  position: relative;
  flex-shrink: 0;
  width: 60px;
  height: 60px;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid white;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.btn-remove-photo {
  position: absolute;
  top: -4px;
  right: -4px;
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}

.camera-controls {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 32px;
  padding-bottom: max(24px, env(safe-area-inset-bottom));
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.6));
}

.btn-close-camera {
  width: 48px;
  height: 48px;
  border: none;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.2);
  color: white;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.3);
  }
}

.btn-capture {
  width: 72px;
  height: 72px;
  border: none;
  border-radius: 50%;
  background-color: transparent;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &.capturing {
    pointer-events: none;

    .capture-center {
      transform: scale(0.8);
    }
  }
}

.capture-ring {
  width: 72px;
  height: 72px;
  border: 4px solid white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s ease;

  &:active {
    transform: scale(0.95);
  }
}

.capture-center {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: white;
  transition: all 0.15s ease;

  .btn-capture:active & {
    background-color: #e0e0e0;
  }
}

.btn-validate {
  width: 48px;
  height: 48px;
  border: none;
  border-radius: 50%;
  background-color: var(--bs-success, #198754);
  color: white;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #157347;
  }
}

.photo-count {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 20px;
  height: 20px;
  border-radius: 10px;
  background-color: white;
  color: var(--bs-success, #198754);
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
}

.spacer {
  width: 48px;
}

.camera-error .btn-close-camera {
  width: auto;
  height: auto;
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 16px;
}
</style>
